import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';

function Production() {

  document.title = 'Production Logs';

  const { user } = useAuth();
  const [productionLogs, setProductionLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('production_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [summaryStats, setSummaryStats] = useState({
    totalLogs: 0,
    totalQuantity: 0,
    productionByShop: {},
  });
  const [dateFilter, setDateFilter] = useState('all');

  // Define a mapping of shop names to colors
  const shopColors = {
    'NORTH': 'bg-blue-100 text-blue-800',
    'SOUTH': 'bg-green-100 text-green-800',
  };

  // Function to get the chip class based on shop name
  const getShopChipClass = (shopName) => {
    return shopColors[shopName] || shopColors['Unknown'];
  };

  // Helper function to format a date as YYYY-MM-DD
  const formatDateToString = (date) => {
    return date.toISOString().split('T')[0]; // e.g., "2025-03-28"
  };

  useEffect(() => {
    const fetchProductionLogs = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('production_logs')
          .select(`
            log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id,
            jobs(po_number, po_line_number),
            shops(id, name),
            machines(id, name)
          `)
          .order('production_date', { ascending: false });
        if (error) throw new Error(`Production logs fetch error: ${error.message}`);

        const logs = Array.isArray(data) ? data : [];
        setProductionLogs(logs);
        setFilteredLogs(logs);

        // Calculate summary statistics
        const totalLogs = logs.length;
        const totalQuantity = logs.reduce((sum, log) => sum + (log.quantity_produced || 0), 0);
        const productionByShop = logs.reduce((acc, log) => {
          const shopName = log.shops?.name || 'Unknown';
          acc[shopName] = (acc[shopName] || 0) + (log.quantity_produced || 0);
          return acc;
        }, {});

        setSummaryStats({
          totalLogs,
          totalQuantity,
          productionByShop,
        });
      } catch (err) {
        console.error('Error fetching production logs:', err.message);
        toast.error(`Failed to fetch production logs: ${err.message}`);
        setProductionLogs([]);
        setFilteredLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductionLogs();
}, [user]);

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedLogs = [...filteredLogs].sort((a, b) => {
      let aValue = a[field] || '';
      let bValue = b[field] || '';
      if (field === 'production_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (field === 'quantity_produced') {
        aValue = aValue || 0;
        bValue = bValue || 0;
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (field === 'po_number') {
        aValue = a.jobs?.po_number || '';
        bValue = b.jobs?.po_number || '';
      }
      if (field === 'po_line_number') {
        aValue = a.jobs?.po_line_number || 0;
        bValue = b.jobs?.po_line_number || 0;
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (field === 'shop') {
        aValue = a.shops?.name || '';
        bValue = b.shops?.name || '';
      }
      if (field === 'machine') {
        aValue = a.machines?.name || '';
        bValue = b.machines?.name || '';
      }
      aValue = typeof aValue === 'string' ? aValue : String(aValue);
      bValue = typeof bValue === 'string' ? bValue : String(bValue);
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setFilteredLogs(sortedLogs);
  };

  const handleDateFilter = (filter) => {
    setDateFilter(filter);
    let filtered = [...productionLogs];

    // Get the current date in UTC (since production_date is a DATE type, we treat it as UTC)
    const now = new Date(); // Hardcoded for testing as per requirement
    const todayStr = formatDateToString(now); // e.g., "2025-03-28"

    if (filter === 'today') {
      filtered = productionLogs.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr === todayStr;
      });
    } else if (filter === 'last7days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const sevenDaysAgoStr = formatDateToString(sevenDaysAgo);
      filtered = productionLogs.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= sevenDaysAgoStr && logDateStr <= todayStr;
      });
    } else if (filter === 'last30days') {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const thirtyDaysAgoStr = formatDateToString(thirtyDaysAgo);
      filtered = productionLogs.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= thirtyDaysAgoStr && logDateStr <= todayStr;
      });
    } else if (filter === 'thismonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfMonthStr = formatDateToString(startOfMonth);
      filtered = productionLogs.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= startOfMonthStr && logDateStr <= todayStr;
      });
    } else if (filter === 'last3months') {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      const threeMonthsAgoStr = formatDateToString(threeMonthsAgo);
      filtered = productionLogs.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= threeMonthsAgoStr && logDateStr <= todayStr;
      });
    }

    setFilteredLogs(filtered);

    // Update summary statistics based on filtered logs
    const totalLogs = filtered.length;
    const totalQuantity = filtered.reduce((sum, log) => sum + (log.quantity_produced || 0), 0);
    const productionByShop = filtered.reduce((acc, log) => {
      const shopName = log.shops?.name || 'Unknown';
      acc[shopName] = (acc[shopName] || 0) + (log.quantity_produced || 0);
      return acc;
    }, {});

    setSummaryStats({
      totalLogs,
      totalQuantity,
      productionByShop,
    });
  };

  const handleDeleteProductionLog = async (logId) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this production log?');
    if (!userConfirmed) return;

    try {
      const { error: deleteError } = await supabase
        .from('production_logs')
        .delete()
        .eq('log_id', logId);
      if (deleteError) throw new Error(`Failed to delete production log: ${deleteError.message}`);

      const updatedLogs = productionLogs.filter(log => log.log_id !== logId);
      setProductionLogs(updatedLogs);
      handleDateFilter(dateFilter); // Reapply the filter to update filteredLogs and summary stats
      window.location.reload(); // Reload the page to reflect changes
    } catch (err) {
      console.error('Error deleting production log:', err);
      toast.error(`Failed to delete production log: ${err.message}`);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Production Logs</h1>
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Total Logs</h3>
            <p className="text-2xl font-bold text-gray-700">{summaryStats.totalLogs}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Total Quantity Produced</h3>
            <p className="text-2xl font-bold text-gray-700">{summaryStats.totalQuantity.toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Production by Shop</h3>
            <div className="mt-2 space-x-1 flex">
              {Object.entries(summaryStats.productionByShop).map(([shop, quantity]) => (
                <p key={shop} className="text-sm text-gray-700">
                  <span className="font-medium"><strong>{shop}</strong>:</span> {quantity.toLocaleString()}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Date Filter Dropdown */}
        <div className="bg-white p-4 rounded-lg mb-6 shadow-sm border border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
              <select
                value={dateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="thismonth">This Month</option>
                <option value="last3months">Last 3 Months</option>
              </select>
            </div>
          </div>
        </div>

        {/* Production Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('po_number')}
                >
                  PO Number {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('po_line_number')}
                >
                  Line # {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('part_number')}
                >
                  Part Number {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('quantity_produced')}
                >
                  QTY Produced {sortField === 'quantity_produced' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('production_date')}
                >
                  Date {sortField === 'production_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('shop')}
                >
                  Shop {sortField === 'shop' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('machine')}
                >
                  Machine {sortField === 'machine' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.jobs?.po_number ? (
                        <Link
                          to={`/jobs/${log.jobs.po_number}`}
                          className="text-indigo-600 hover:text-indigo-800 transition duration-150"
                        >
                          {log.jobs.po_number}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.jobs?.po_line_number || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Link
                        to={`/part/${log.part_number}`}
                        className="text-indigo-600 hover:text-indigo-800 transition duration-150"
                      >
                        {log.part_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.quantity_produced}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(log.production_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getShopChipClass(log.shops?.name || 'Unknown')}`}>
                        {log.shops?.name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.machines?.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteProductionLog(log.log_id)}
                        className="text-red-600 hover:text-red-800 transition duration-150 flex items-center space-x-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        <span>Delete</span>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No production logs found for the selected date range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Production;